import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess } from '../App';
import { DollarSign, Package, TrendingUp, Clock } from 'lucide-react';

const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm';

function Stat({ label, value, color, icon: Icon, subtitle }) {
  return (
    <div className="bg-white rounded-xl border p-5 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color || 'text-gray-800'}`}>{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function InventoryPay() {
  const { user } = useAuth();
  const { userId } = useParams(); // optional — admin viewing someone else
  const isAdmin = hasManagerAccess(user);
  const viewingSelf = !userId || Number(userId) === user?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    paid_date: new Date().toISOString().split('T')[0],
    notes: 'Inventory pay',
  });
  const [savingPayout, setSavingPayout] = useState(false);

  const load = () => {
    setLoading(true);
    const url = userId ? `/inventory/pay/${userId}` : '/inventory/pay';
    api.get(url).then(setData).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, [userId]);

  const handleLogPayout = async (e) => {
    e.preventDefault();
    if (!payoutForm.amount || !data?.user?.id) return;
    setSavingPayout(true);
    try {
      await api.post('/commissions/payout', {
        salesman_id: data.user.id,
        amount: Number(payoutForm.amount),
        paid_date: payoutForm.paid_date,
        notes: payoutForm.notes,
      });
      setPayoutForm({ amount: '', paid_date: new Date().toISOString().split('T')[0], notes: 'Inventory pay' });
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingPayout(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;
  if (!data) return <p className="text-gray-400 text-sm">No pay data available.</p>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-7 h-7 text-nova-600" />
        <h1 className="text-2xl font-bold">
          {viewingSelf ? 'My Pay' : `${data.user.full_name}'s Pay`}
        </h1>
      </div>

      {/* Rate banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-4 py-3 mb-5 flex items-center gap-2">
        <Package className="w-4 h-4 text-green-600" />
        <p className="text-sm text-green-800">
          Pay rate: <span className="font-bold">{fmt(data.rate)}</span> per channel added to stock.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat
          label="Channels Added"
          value={(data.totalChannels || 0).toLocaleString()}
          color="text-gray-800"
          icon={Package}
          subtitle="Lifetime"
        />
        <Stat
          label="Total Earned"
          value={fmt(data.totalEarned)}
          color="text-green-600"
          icon={TrendingUp}
        />
        <Stat
          label="Paid Out"
          value={fmt(data.totalPaid)}
          color="text-blue-600"
        />
        <Stat
          label="Balance Owed"
          value={fmt(data.balanceOwed)}
          color={data.balanceOwed > 0 ? 'text-orange-500' : 'text-gray-400'}
          icon={Clock}
        />
      </div>

      {/* Transactions table */}
      <div className="bg-white rounded-xl border overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-700">Restock History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Channel Color</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Channels Added</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Feet</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Pay Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.transactions.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{t.created_at?.split(' ')[0]}</td>
                  <td className="px-4 py-3 font-medium">{t.color_name || '—'}</td>
                  <td className="px-4 py-3 text-right">{t.quantity_change}</td>
                  <td className="px-4 py-3 text-right text-gray-500">
                    {(t.quantity_change * (t.feet_per_channel || 6)).toLocaleString()} ft
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">
                    {fmt(t.quantity_change * data.rate)}
                  </td>
                </tr>
              ))}
              {data.transactions.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No restocks yet</td></tr>
              )}
              {data.transactions.length > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={2} className="px-4 py-3 text-right text-gray-600">Total</td>
                  <td className="px-4 py-3 text-right">{data.totalChannels.toLocaleString()}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right text-green-600">{fmt(data.totalEarned)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout history */}
      <div className="bg-white rounded-xl border overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-700">Payout History</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Notes</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Paid By</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.payouts.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">{p.paid_date}</td>
                <td className="px-4 py-3 font-semibold text-blue-600">{fmt(p.amount)}</td>
                <td className="px-4 py-3 text-gray-400">{p.notes || '-'}</td>
                <td className="px-4 py-3 text-gray-500">{p.paid_by_name || '-'}</td>
              </tr>
            ))}
            {data.payouts.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No payouts recorded</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Record payout (admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold text-gray-700 mb-4">Record Payout</h2>
          <form onSubmit={handleLogPayout}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Amount</label>
                <input
                  type="number" step="0.01" required
                  value={payoutForm.amount}
                  onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                  className={inputCls}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date" required
                  value={payoutForm.paid_date}
                  onChange={e => setPayoutForm(f => ({ ...f, paid_date: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  value={payoutForm.notes}
                  onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingPayout || !payoutForm.amount}
              className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
              {savingPayout ? 'Saving…' : 'Record Payout'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
